/**
 * Actual vs strategic target over a grain timeline.
 * Empty buckets stay on the axis so a multi-year strategic line is continuous.
 */

import React from 'react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import { fmtKpiWithUnit, QUARTERLY_RING, STRATEGIC_RING, type KpiTile } from './types';

export interface HistoryChartPoint {
  key: string;
  label: string;
  actual: number | null;
  strategic: number | null;
}

interface KpiHistoryChartProps {
  points: HistoryChartPoint[];
  kpi: Pick<KpiTile, 'type' | 'unit'>;
  t: (key: string) => string;
}

export const KpiHistoryChart: React.FC<KpiHistoryChartProps> = ({ points, kpi, t }) => {
  const format = (value: number | null | undefined) => fmtKpiWithUnit(kpi, value);
  const tickCount = Math.max(2, Math.min(8, points.length));
  const interval = Math.max(0, Math.ceil(points.length / tickCount) - 1);

  return (
    <div className="h-64 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={points} margin={{ top: 8, right: 12, left: 8, bottom: 4 }}>
          <CartesianGrid stroke="var(--color-border)" strokeDasharray="3 3" />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 10 }}
            interval={interval}
            axisLine={{ stroke: 'var(--color-border)' }}
            tickLine={false}
          />
          <YAxis
            tick={{ fontSize: 10 }}
            tickFormatter={(value: number) => format(value)}
            width={64}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip
            contentStyle={{ fontSize: 11 }}
            formatter={(value: number, name: string) => [format(value), name]}
          />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <Line
            type="monotone"
            dataKey="actual"
            name={t('kpi.historyActual') || 'Actual'}
            stroke={QUARTERLY_RING}
            strokeWidth={2}
            dot={{ r: 3 }}
            connectNulls={false}
            isAnimationActive={false}
          />
          <Line
            type="monotone"
            dataKey="strategic"
            name={t('kpi.historyStrategic') || 'Strategic target'}
            stroke={STRATEGIC_RING}
            strokeWidth={2}
            strokeDasharray="6 4"
            dot={false}
            connectNulls
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};
